import type { RawParams } from "@uirouter/react";
import styled from "styled-components";

import type { AnyObject, ColumnsType } from "@similarweb/pro-logic-components";
import { TextCell, TooltipTextAnchorCell, findAndSetColumnSortOrder } from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";

import { TrafficShare } from "app/components/React/Table/cells";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, spendFilter } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import type { dateForTooltip } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { AdIntelligenceController } from "../shared/types/types";
import type { ISortProps } from "../utils/utils";
import { NA_VALUE, getChangeCellValues, hasSearchAndAdsCanary } from "../utils/utils";

export const IndustryFilterContainer = styled.div`
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

type GetColumns = (
    onCategoryFilterChange: (category: { id?: string }, param: keyof RawParams) => void,
    hasAdIntelMetrics: boolean,
    controller: AdIntelligenceController,
    resource: AdIntelligenceController,
    totalRecords: number,
    currentPage: number,
    pageSize: number,
    sort: ISortProps,
    durationTooltipParams: dateForTooltip,
    isWindow: boolean,
) => ColumnsType<AnyObject>;

export const getColumns: GetColumns = (
    onCategoryFilterChange,
    hasAdIntelMetrics,
    controller,
    resource,
    totalRecords,
    currentPage,
    pageSize,
    sort,
    durationTooltipParams,
    isWindow,
) => {
    const isSearchCanary = hasSearchAndAdsCanary();

    const getWebsiteLink = (url: string) => {
        if (controller === "advertiser") {
            return publisherAnalysisNavigation.overview.getLink({ ...proNavigator.getParams(), key: url });
        }
        return displayAdvertisingNavigation.overview.getLink({ ...proNavigator.getParams(), key: url });
    };

    const renderNoDataTooltip = () => (
        <Tooltip
            content={translate(`ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`)}
            enabled={!hasAdIntelMetrics}
        >
            <span>
                <TextCell>{NA_VALUE}</TextCell>
            </span>
        </Tooltip>
    );

    return (
        [
            {
                title: "",
                align: "center",
                fixed: "left",
                key: "index",
                ...getIndexCellPropsForTableV2(currentPage, pageSize, (record) => !record.isChild),
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.${resource}s.column.title`, {
                    totalRecords: numberFilter()(totalRecords),
                }),
                tooltipTitle: translate(
                    `ad.intelligence.display.${controller}.${resource}s.${resource}s.column.tooltip.title`,
                ),
                tooltipSubtitle: translate(
                    `ad.intelligence.display.${controller}.${resource}s.${resource}s.column.tooltip`,
                ),
                dataIndex: resource,
                displayName: translate(`ad.intelligence.${controller}.${resource}s.${resource}s.column.picker.title`),
                width: 200,
                fixed: "left",
                hiddenInColumnPicker: true,
                render: ({ url, favicon }: Record<string, string> = {}, row) => (
                    <CoreWebsiteCell
                        domain={url}
                        icon={favicon}
                        textAndIconMargin="8px"
                        showToolTip
                        internalLink={getWebsiteLink(url)}
                        trackInternalLink={(e) => {
                            e.stopPropagation();
                            allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/${url}`);
                        }}
                        childrenCount={row.children?.length}
                        spacingDomainAndAddons={false}
                        TooltipComponent={({ children }) => (
                            <Tooltip
                                content={translate("ad.intelligence.overview.publishers.table.tooltip", { site: url })}
                                placement="top"
                            >
                                {children}
                            </Tooltip>
                        )}
                    />
                ),
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.category.column.title`),
                tooltipTitle: translate(`ad.intelligence.${controller}.${resource}s.category.column.tooltipTitle`),
                tooltipSubtitle: translate(
                    `ad.intelligence.${controller}.${resource}s.category.column.tooltipSubtitle`,
                ),
                dataIndex: "category",
                width: 180,
                hidden: true,
                render: (category: { id: string; name: string }) => (
                    <Tooltip
                        content={translate(`ad.intelligence.${controller}.${resource}s.category.filter.by`, {
                            category: category.name,
                        })}
                        enabled={!hasAdIntelMetrics ? !!category.id : true}
                    >
                        <IndustryFilterContainer>
                            {category.id ? (
                                <TooltipTextAnchorCell
                                    onClick={() => onCategoryFilterChange({ id: category.id }, "category")}
                                    dataAutomation="category"
                                    tooltipContent={translate(
                                        "ad.intelligence.advertiser.publishers.category.filter.by",
                                        {
                                            category: category.name,
                                        },
                                    )}
                                >
                                    {category.name}
                                </TooltipTextAnchorCell>
                            ) : (
                                <TextCell>{NA_VALUE}</TextCell>
                            )}
                        </IndustryFilterContainer>
                    </Tooltip>
                ),
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.rank.column.title`),
                tooltipTitle: translate(`ad.intelligence.${controller}.${resource}s.rank.column.tooltipTitle`),
                tooltipSubtitle: translate(`ad.intelligence.${controller}.${resource}s.rank.column.tooltipSubtitle`),
                dataIndex: "rank",
                width: 90,
                align: "right",
                hidden: true,
                render: (rank: string) => (
                    <TextCell dataAutomation="rank">{rank ? `#${numberFilter()(rank)}` : NA_VALUE}</TextCell>
                ),
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.spend.column.title`),
                align: "center",
                key: "spendGroup",
                children: [
                    {
                        title: translate(`ad.intelligence.${controller}.${resource}s.spend.share.column.title`),
                        tooltipTitle: translate(
                            `ad.intelligence.display.${controller}.${resource}s.spend.share.column.tooltip.title`,
                        ),
                        tooltipSubtitle: translate(
                            `ad.intelligence.display.${controller}.${resource}s.spend.share.column.tooltip`,
                        ),
                        dataIndex: "spend",
                        width: hasAdIntelMetrics ? 172 : 60,
                        sorter: hasAdIntelMetrics,
                        render: ({ total: _total, value }, row) => {
                            if (!value || value === 0) {
                                return renderNoDataTooltip();
                            }
                            return (
                                <Tooltip
                                    content={translate(
                                        `ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`,
                                    )}
                                    enabled={!hasAdIntelMetrics}
                                >
                                    <span>
                                        <TrafficShare
                                            value={value}
                                            row={row}
                                            undefinedSign={NA_VALUE}
                                            thresholdTrafficShareWithVisitsPercentage={
                                                THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            );
                        },
                    },
                    ...(isSearchCanary
                        ? [
                              {
                                  title: translate(
                                      `ad.intelligence.${controller}.${resource}s.spend.value.column.title`,
                                  ),
                                  tooltipTitle: translate(
                                      `ad.intelligence.display.${controller}.${resource}s.spend.value.column.tooltip.title`,
                                  ),
                                  tooltipSubtitle: translate(
                                      `ad.intelligence.display.${controller}.${resource}s.spend.value.column.tooltip`,
                                  ),
                                  dataIndex: "spend",
                                  width: 90,
                                  align: "right" as const,
                                  render: ({ total }: { total: number }) => {
                                      if (!total || total === 0) {
                                          return renderNoDataTooltip();
                                      }
                                      return (
                                          <Tooltip
                                              content={translate(
                                                  `ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`,
                                              )}
                                              enabled={!hasAdIntelMetrics}
                                          >
                                              <span>
                                                  <TextCell dataAutomation="spend-value">
                                                      {spendFilter(total, abbrNumberFilter())}
                                                  </TextCell>
                                              </span>
                                          </Tooltip>
                                      );
                                  },
                              },
                          ]
                        : []),
                    {
                        title: translate(`ad.intelligence.${controller}.${resource}s.spend.change.column.title`),
                        tooltipTitle: translate(
                            `ad.intelligence.display.${controller}.${resource}s.spend.change.column.tooltip.title`,
                        ),
                        tooltipSubtitle: isWindow
                            ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                            : translate(
                                  `ad.intelligence.display.${controller}.${resource}s.spend.change.column.tooltip`,
                                  durationTooltipParams,
                              ),
                        dataIndex: "spendChange",
                        width: hasAdIntelMetrics ? 80 : 65,
                        align: "right",
                        render: ({ value, isNew }) => {
                            const { changeState, precision } = getChangeCellValues({ value, isNew });
                            return (
                                <Tooltip
                                    content={translate(
                                        `ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`,
                                    )}
                                    enabled={!hasAdIntelMetrics}
                                >
                                    <span>
                                        {value !== undefined ? (
                                            <ChangeStateCell
                                                value={{
                                                    change: value,
                                                    changeState,
                                                }}
                                                undefinedSign={NA_VALUE}
                                                isPercentage={true}
                                                cellFilter={`simplePercentage:${precision}`}
                                            />
                                        ) : (
                                            <TextCell>{NA_VALUE}</TextCell>
                                        )}
                                    </span>
                                </Tooltip>
                            );
                        },
                    },
                ],
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.impressions.column.title`),
                align: "center",
                key: "impressionsGroup",
                children: [
                    {
                        title: translate(`ad.intelligence.${controller}.${resource}s.impressions.share.column.title`),
                        tooltipTitle: translate(
                            `ad.intelligence.display.${controller}.${resource}s.impressions.share.column.tooltip.title`,
                        ),
                        tooltipSubtitle: translate(
                            `ad.intelligence.display.${controller}.${resource}s.impressions.share.column.tooltip`,
                        ),
                        dataIndex: "impressions",
                        width: hasAdIntelMetrics ? 172 : 60,
                        sorter: hasAdIntelMetrics,
                        render: ({ total: _total, value }, row) => {
                            if (!value || value === 0) {
                                return renderNoDataTooltip();
                            }
                            return (
                                <Tooltip
                                    content={translate(
                                        `ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`,
                                    )}
                                    enabled={!hasAdIntelMetrics}
                                >
                                    <span>
                                        <TrafficShare
                                            value={value}
                                            row={row}
                                            undefinedSign={NA_VALUE}
                                            thresholdTrafficShareWithVisitsPercentage={
                                                THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            );
                        },
                    },
                    ...(isSearchCanary
                        ? [
                              {
                                  title: translate(
                                      `ad.intelligence.${controller}.${resource}s.impressions.value.column.title`,
                                  ),
                                  tooltipTitle: translate(
                                      `ad.intelligence.display.${controller}.${resource}s.impressions.value.column.tooltip.title`,
                                  ),
                                  tooltipSubtitle: translate(
                                      `ad.intelligence.display.${controller}.${resource}s.impressions.value.column.tooltip`,
                                  ),
                                  dataIndex: "impressions",
                                  width: 90,
                                  align: "right" as const,
                                  render: ({ total }: { total: number }) => {
                                      if (!total || total === 0) {
                                          return renderNoDataTooltip();
                                      }
                                      return (
                                          <Tooltip
                                              content={translate(
                                                  `ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`,
                                              )}
                                              enabled={!hasAdIntelMetrics}
                                          >
                                              <span>
                                                  <TextCell dataAutomation="impressions-value">
                                                      {abbrNumberFilter()(total)}
                                                  </TextCell>
                                              </span>
                                          </Tooltip>
                                      );
                                  },
                              },
                          ]
                        : []),
                    {
                        title: translate(`ad.intelligence.${controller}.${resource}s.impressions.change.column.title`),
                        tooltipTitle: translate(
                            `ad.intelligence.display.${controller}.${resource}s.impressions.change.column.tooltip.title`,
                        ),
                        tooltipSubtitle: isWindow
                            ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                            : translate(
                                  `ad.intelligence.display.${controller}.${resource}s.impressions.change.column.tooltip`,
                                  durationTooltipParams,
                              ),
                        dataIndex: "impressionsChange",
                        width: hasAdIntelMetrics ? 80 : 65,
                        align: "right",
                        render: ({ value, isNew }) => {
                            const { changeState, precision } = getChangeCellValues({ value, isNew });
                            return (
                                <Tooltip
                                    content={translate(
                                        `ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`,
                                    )}
                                    enabled={!hasAdIntelMetrics}
                                >
                                    <span>
                                        {value !== undefined ? (
                                            <ChangeStateCell
                                                value={{
                                                    change: value,
                                                    changeState,
                                                }}
                                                undefinedSign={NA_VALUE}
                                                isPercentage={true}
                                                cellFilter={`simplePercentage:${precision}`}
                                            />
                                        ) : (
                                            <TextCell>{NA_VALUE}</TextCell>
                                        )}
                                    </span>
                                </Tooltip>
                            );
                        },
                    },
                ],
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.visits.column.title`),
                align: "center",
                key: "visitsGroup",
                children: [
                    {
                        title: translate(`ad.intelligence.${controller}.${resource}s.visits.share.column.title`),
                        tooltipTitle: translate(
                            `ad.intelligence.${controller}.${resource}s.visits.share.column.tooltipTitle`,
                        ),
                        tooltipSubtitle: translate(
                            `ad.intelligence.${controller}.${resource}s.visits.share.column.tooltipSubtitle`,
                        ),
                        dataIndex: "visits",
                        key: "visits",
                        sorter: true,
                        width: 172,
                        render: (visits: { totalVisits: undefined; value: number }, row) => {
                            if (!visits.value || visits.value === 0) {
                                return renderNoDataTooltip();
                            }
                            return (
                                <TrafficShare
                                    value={visits.value}
                                    row={row}
                                    undefinedSign={NA_VALUE}
                                ></TrafficShare>
                            );
                        },
                    },
                    {
                        title: translate(`ad.intelligence.${controller}.${resource}s.visits.change.column.title`),
                        tooltipTitle: translate(
                            `ad.intelligence.${controller}.${resource}s.visits.change.column.tooltipTitle`,
                        ),
                        tooltipSubtitle: isWindow
                            ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                            : translate(
                                  `ad.intelligence.${controller}.${resource}s.visits.change.column.tooltipSubtitle`,
                                  durationTooltipParams,
                              ),
                        dataIndex: "visitsChange",
                        width: 80,
                        align: "right",
                        render: ({ value, isNew }) => {
                            const { changeState, precision } = getChangeCellValues({ value, isNew });

                            return value !== undefined ? (
                                <ChangeStateCell
                                    value={{
                                        change: value,
                                        changeState,
                                    }}
                                    undefinedSign={NA_VALUE}
                                    isPercentage={true}
                                    cellFilter={`simplePercentage:${precision}`}
                                />
                            ) : (
                                <TextCell>{NA_VALUE}</TextCell>
                            );
                        },
                    },
                ],
            },
        ] as ColumnsType<AnyObject>
    ).map((col) => findAndSetColumnSortOrder(col, sort.sort, sort.asc?.toString() === "true"));
};
